import { Container, Graphics } from "pixi.js";

import { AbstractSymbol } from "../../../abstracts/elements/AbstractSymbol";
import { AbstractGameMode } from "../../../abstracts/flow/AbstractGameMode";
import { AbstractView } from "../../../abstracts/modules/AbstractView";
import { flat } from "../../../abstracts/utils/ArrayUtils";
import { generateScaledSprite } from "../../../abstracts/utils/GraphicUtils";
import { Apocalypse } from "../../Apocalypse";
import {
    BASE_REELSET,
    FREESPIN_BASE_REELSET,
    devDebugConfig,
    reelsConfig,
    symbolConfig,
} from "../../config/ApocalypseConfig";
import { ApocalypseLowSymbolNames, ApocalypseSymbolNames } from "../../types/ApocalypseEnums";
import { fsMatrix } from "../../utils/ApocalypseJSUtils";
import { ApocalypseFsContainer } from "./fs/ApocalypseFsContainer";
import { ApocalypseFsReel } from "./fs/ApocalypseFsReel";
import { ApocalypseNormalContainer } from "./normal/ApocalypseNormalContainer";
import ApocalypseReel from "./normal/ApocalypseReel";
import { ApocalypseSymbol } from "./symbols/ApocalypseSymbol";
import { getSymbolFromPool, returnSymbolToPool } from "./symbols/ApocalypseSymbolsPool";

export class ApocalypseReelsView extends AbstractView {
    private normalModeContainer: ApocalypseNormalContainer;
    private readonly normalReels: ApocalypseReel[];

    private fsModeContainer: ApocalypseFsContainer;
    private readonly fsReels: ApocalypseFsReel[];

    private blockersContainer: Container;

    constructor() {
        super(`ApocalypseReelsView`);

        this.normalReels = [];
        this.fsReels = [];
    }

    protected initGraphics(): void {
        this.normalModeContainer = new ApocalypseNormalContainer();
        this.addChild(this.normalModeContainer);

        this.fsModeContainer = new ApocalypseFsContainer();

        this.addChild(this.fsModeContainer);

        if (devDebugConfig.showReelDebugGraphics) {
            if (devDebugConfig.disableReelMask) {
                this.scale.set(0.6, 0.6);
                this.position.set(184, 300);
            } else {
                this.scale.set(1, 1);
                this.position.set(reelsConfig.viewXPosition, reelsConfig.viewYPosition);
            }
        } else {
            this.scale.set(1.12, 1.12);
            this.position.set(reelsConfig.viewXPosition, reelsConfig.viewYPosition);
        }

        this.blockersContainer = new Container();
        this.blockersContainer.name = `ReelsBlockersContainer`;
        this.blockersContainer.x = reelsConfig.reelsContainerX;
        this.blockersContainer.y = reelsConfig.reelsContainerY - 107;
    }

    public override onResize(): void {
        this.fsModeContainer.onOrientationChange();
    }

    public enableIdle() {}

    public disableIdle() {}

    protected onGameModeUpdate(): void {
        switch (this.gameMode) {
            case AbstractGameMode.FREESPIN:
            case AbstractGameMode.SUPER_FREESPIN:
            case AbstractGameMode.FREESPIN_BETTER: {
                const newEvaluatedArea = Apocalypse.model.data.evaluatedArea.map((reel) => {
                    return [`DUMP`, ...reel, `DUMP`];
                });

                const flatEvaluatedData = flat(fsMatrix(newEvaluatedArea));
                const flatWinMatrixFS = flat(fsMatrix(Apocalypse.model.data.winMatrixFS));

                this.fsReels.forEach((reel, reelIndex) => {
                    let symbolName: ApocalypseSymbolNames = ApocalypseSymbolNames.BLANK;

                    if (flatEvaluatedData[reelIndex] === ApocalypseSymbolNames.SCATTER) {
                        this.fsModeContainer.addFakeScatter(reelIndex);

                        switch (flatWinMatrixFS[reelIndex]) {
                            case 40:
                                symbolName = ApocalypseLowSymbolNames.L1;
                                break;
                            case 20:
                                symbolName = ApocalypseLowSymbolNames.L2;
                                break;
                            case 10:
                                symbolName = ApocalypseLowSymbolNames.L3;
                                break;
                            case 5:
                                symbolName = ApocalypseLowSymbolNames.L4;
                                break;

                            default:
                                symbolName = ApocalypseLowSymbolNames.L5;
                                break;
                        }
                    }

                    reel.updateCurrentSymbols([symbolName]);
                });

                this.removeChild(this.normalModeContainer);

                this.addChild(this.fsModeContainer);

                // this.removeChild(this.logo);
                this.normalModeContainer.reset();

                break;
            }

            default:
                this.removeChild(this.fsModeContainer);
                this.fsModeContainer.reset();

                this.fsReels.forEach((reel) => {
                    reel.reset();
                });

                this.addChild(this.normalModeContainer);

                break;
        }
    }

    public generateInitialReels(serverReels: string[][]): void {
        //this is pre rendering the symbols to avoid the first frame lag
        const pregeneratedSymbols: AbstractSymbol[] = [];

        for (const key in ApocalypseSymbolNames) {
            if (Object.prototype.hasOwnProperty.call(ApocalypseSymbolNames, key)) {
                const value = ApocalypseSymbolNames[key as keyof typeof ApocalypseSymbolNames];
                for (let i = 0; i < 3; i++) {
                    const symbol = getSymbolFromPool(value);

                    pregeneratedSymbols.push(symbol);
                }
            }
        }

        pregeneratedSymbols.forEach((symbol) => returnSymbolToPool(symbol));

        switch (this.gameMode) {
            case AbstractGameMode.FREESPIN:
            case AbstractGameMode.SUPER_FREESPIN:
            case AbstractGameMode.FREESPIN_BETTER: {
                //generating freespin reels with server data
                this.initializeFreespinReels(serverReels);

                //generating normal reels with Base reels. Please see ApocalypseConfig.ts
                this.initializeNormalReels(BASE_REELSET);

                break;
            }

            default: {
                //generating normal reels with server data
                this.initializeNormalReels(serverReels);

                //generating freespin reels with Base reels. Please see ApocalypseConfig.ts
                this.initializeFreespinReels(FREESPIN_BASE_REELSET);

                break;
            }
        }
    }

    private initializeNormalReels(initReels: string[][]) {
        initReels.forEach((reel: string[], index) => {
            const normalReel = new ApocalypseReel(index, reel, true);
            normalReel.x =
                (symbolConfig.symbolWidth + reelsConfig.reelSpacing) * normalReel.reelId +
                reelsConfig.reelSpacingOffset[normalReel.reelId];

            normalReel.gameMode = this.gameMode;

            this.normalModeContainer.reelsContainer.addChild(normalReel);
            this.normalReels.push(normalReel);
        });

        this.normalModeContainer.reels = this.normalReels;
    }

    private initializeFreespinReels(initReels: string[][]) {
        let reelsTotalCounter: number = 0;
        let clonedInitReels: string[][] = fsMatrix(initReels);

        const fsWinMatrix = flat(fsMatrix(Apocalypse.model.initData.winMatrixFS));
        const fsPersistenceMap = flat(fsMatrix(Apocalypse.model.persistenceMap));

        clonedInitReels.forEach((reel: string[], reelIndex) => {
            const tempY = (symbolConfig.symbolHeight + reelsConfig.reelSpacing) * (reelIndex - 1);

            let tempX: number;

            reel.forEach((symbol, symbolIndex) => {
                tempX = (symbolConfig.symbolWidth + reelsConfig.reelSpacing) * symbolIndex;

                const freespinReel = new ApocalypseFsReel(reelsTotalCounter, [symbol], true);
                freespinReel.x = tempX;
                freespinReel.y = tempY;

                this.fsReels.push(freespinReel);
                this.fsModeContainer.reels = this.fsReels;
                if (
                    freespinReel.isLocked &&
                    freespinReel.reelId > reelsConfig.reelsNumber - 1 &&
                    freespinReel.reelId <=
                        reelsConfig.reelsNumber * (reelsConfig.fsRowsNumber - 1) - 1
                ) {
                    this.fsModeContainer.getLockLowReelAnimation(
                        reelsTotalCounter,
                        undefined,
                        true
                    );

                    if (Apocalypse.model.isRestore) {
                        (
                            this.fsReels[reelsTotalCounter].getSymbolAtIndex(1) as ApocalypseSymbol
                        ).getUpdateMultiplierAnimation(fsWinMatrix[reelsTotalCounter], true);
                    }
                } else if (
                    freespinReel.isLocked &&
                    (freespinReel.reelId < reelsConfig.reelsNumber ||
                        freespinReel.reelId >=
                            reelsConfig.reelsNumber * (reelsConfig.fsRowsNumber - 1)) &&
                    fsPersistenceMap.length > 0 &&
                    fsPersistenceMap[freespinReel.reelId]
                ) {
                    this.fsModeContainer.getLockHighReelAnimation(reelsTotalCounter, true);
                }

                freespinReel.gameMode = this.gameMode;
                this.fsModeContainer.reelsContainer.addChild(freespinReel);

                const reelMaskGr = new Graphics();
                reelMaskGr.name = `fsReelMask${reelsTotalCounter}`;
                reelMaskGr.beginFill(0xffffff);
                reelMaskGr.drawRect(0, 0, symbolConfig.symbolWidth, symbolConfig.symbolHeight);
                reelMaskGr.endFill();
                reelMaskGr.x = freespinReel.x;
                reelMaskGr.y = freespinReel.y;

                freespinReel.mask = reelMaskGr;

                this.fsModeContainer.reelsContainer.addChild(reelMaskGr);

                reelsTotalCounter++;
            });
        });

        const fsReelsOverlayGrid = generateScaledSprite(`apocalypse/Grid`);
        fsReelsOverlayGrid.x = -10;
        fsReelsOverlayGrid.y = -116;
        this.fsModeContainer.reelsContainer.addChild(fsReelsOverlayGrid);
    }

    public getNormalReels(): ApocalypseReel[] {
        return this.normalReels;
    }

    public getFreespinReels(): ApocalypseFsReel[] {
        return this.fsReels;
    }

    public getFreespinContainer(): ApocalypseFsContainer {
        return this.fsModeContainer;
    }

    public getNormalContainer(): ApocalypseNormalContainer {
        return this.normalModeContainer;
    }
}
